import React, { useCallback, useEffect, useRef, useState } from 'react'
import { filesAPI, type FileEntry } from '@hpanel/web-common/api/client'

/**
 * Tenant file manager.
 *
 * History: this page shipped as a "Coming Soon" placeholder with a
 * non-functional notify button, while `/api/files` (list/read/write/upload/
 * download/delete) was fully implemented and even boundary-tested. The QA live
 * validator's vision judge flagged the mismatch on 2026-07-29 — the page
 * returned 200 and emitted no errors, so nothing else could have caught it.
 * Trinity Parity (CLAUDE.md §3) requires the UI to exist alongside the API.
 *
 * Paths are relative to the tenant's home root; the backend enforces the
 * traversal boundary, and the breadcrumb never lets you climb above '/'.
 *
 * §9: every call goes through `filesAPI` (i.e. `fetchAPI`), so empty bodies,
 * non-JSON error pages and network failures surface as clean messages, and the
 * server's own `detail` reaches the user unchanged. The original error is
 * console.error'd before being swallowed.
 */

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`
}

function formatTime(epochSeconds: number): string {
  if (!epochSeconds) return '—'
  return new Date(epochSeconds * 1000).toLocaleString()
}

function isDirectory(entry: FileEntry): boolean {
  return entry.kind === 'dir' || entry.kind === 'directory'
}

/** Join a directory path and a child name into a normalised absolute path. */
function joinPath(dir: string, name: string): string {
  const base = dir.endsWith('/') ? dir.slice(0, -1) : dir
  return `${base}/${name}`
}

/** Parent of a path, never climbing above the root. */
function parentPath(path: string): string {
  const parts = path.split('/').filter(Boolean)
  parts.pop()
  return parts.length ? `/${parts.join('/')}` : '/'
}

function messageOf(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  return 'Something went wrong. Please try again.'
}

export const FileManager: React.FC = () => {
  const [path, setPath] = useState('/')
  const [entries, setEntries] = useState<FileEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Set when the LISTING failed (not upload/delete): the folder's contents
  // are unknown, so it must not also be described as "empty".
  const [listFailed, setListFailed] = useState<{ status?: number } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Name of the file being sent; drives the "Uploading…" indicator.
  const [uploading, setUploading] = useState<string | null>(null)
  // A file the user picked whose name already exists here: waits for Replace/Cancel.
  const [pendingReplace, setPendingReplace] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async (target: string) => {
    setLoading(true)
    setError(null)
    setListFailed(null)
    try {
      const rows = await filesAPI.list(target)
      // Directories first, then alphabetical — stable and predictable.
      const sorted = [...rows].sort((a, b) => {
        const aDir = isDirectory(a)
        const bDir = isDirectory(b)
        if (aDir !== bDir) return aDir ? -1 : 1
        return a.name.localeCompare(b.name)
      })
      setEntries(sorted)
    } catch (err) {
      console.error('FileManager: list failed for', target, err)
      setError(messageOf(err))
      setListFailed({ status: (err as { status?: number })?.status })
      setEntries([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(path)
  }, [path, load])

  const handleDownload = async (entry: FileEntry) => {
    setBusy(entry.name)
    setError(null)
    try {
      const { blob, filename } = await filesAPI.download(joinPath(path, entry.name))
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename || entry.name
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('FileManager: download failed for', entry.name, err)
      setError(messageOf(err))
    } finally {
      setBusy(null)
    }
  }

  const handleDelete = async (entry: FileEntry) => {
    if (!window.confirm(`Delete "${entry.name}"? This cannot be undone.`)) return
    setBusy(entry.name)
    setError(null)
    setNotice(null)
    try {
      await filesAPI.remove(joinPath(path, entry.name))
      setNotice(`Deleted ${entry.name}`)
      await load(path)
    } catch (err) {
      console.error('FileManager: delete failed for', entry.name, err)
      setError(messageOf(err))
    } finally {
      setBusy(null)
    }
  }

  /** Entry point for a picked file: ask first if the name is already taken. */
  const handleUpload = async (file: File) => {
    setError(null)
    setNotice(null)
    if (entries.some((e) => e.name === file.name)) {
      setPendingReplace(file)
      return
    }
    await sendUpload(file, false)
  }

  const sendUpload = async (file: File, overwrite: boolean) => {
    setBusy(file.name)
    setUploading(file.name)
    setError(null)
    setNotice(null)
    try {
      const buffer = await file.arrayBuffer()
      // Chunked conversion: String.fromCharCode(...bigArray) blows the call
      // stack on files of a few MB.
      const bytes = new Uint8Array(buffer)
      let binary = ''
      const CHUNK = 0x8000
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
      }
      await filesAPI.upload(joinPath(path, file.name), btoa(binary), overwrite)
      setNotice(`Uploaded ${file.name}`)
      await load(path)
    } catch (err) {
      console.error('FileManager: upload failed for', file.name, err)
      setError(messageOf(err))
    } finally {
      setBusy(null)
      setUploading(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const cancelReplace = () => {
    setPendingReplace(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const confirmReplace = () => {
    const file = pendingReplace
    setPendingReplace(null)
    if (file) void sendUpload(file, true)
  }

  const handleExtract = async (entry: FileEntry) => {
    setBusy(entry.name)
    setError(null)
    setNotice(null)
    try {
      const result = await filesAPI.extract(joinPath(path, entry.name))
      const files = `${result.extracted} file${result.extracted === 1 ? '' : 's'}`
      const skipped = result.skipped.length
        ? `. Skipped ${result.skipped.length}: ${result.skipped
            .slice(0, 5)
            .map((s) => `${s.name} (${s.reason})`)
            .join(', ')}${result.skipped.length > 5 ? ', …' : ''}`
        : ''
      setNotice(`Extracted ${files} from ${entry.name}${skipped}`)
      await load(path)
    } catch (err) {
      console.error('FileManager: extract failed for', entry.name, err)
      setError(messageOf(err))
    } finally {
      setBusy(null)
    }
  }

  const crumbs = path.split('/').filter(Boolean)

  return (
    <div data-testid="file-manager-page" className="space-y-6">
      <div>
        <h1
          data-testid="page-title"
          className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100"
        >
          Files
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Browse, upload, download, extract .zip archives and delete files in your home tree. The same
          files are visible over SFTP and to your site's web server.
        </p>
      </div>

      {/* Breadcrumb */}
      <nav data-testid="file-breadcrumb" className="flex flex-wrap items-center gap-1 text-sm">
        <button
          type="button"
          onClick={() => setPath('/')}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          home
        </button>
        {crumbs.map((crumb, index) => {
          const target = `/${crumbs.slice(0, index + 1).join('/')}`
          return (
            <span key={target} className="flex items-center gap-1">
              <span className="text-gray-400">/</span>
              <button
                type="button"
                onClick={() => setPath(target)}
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                {crumb}
              </button>
            </span>
          )
        })}
      </nav>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="file-upload-button"
          disabled={uploading !== null}
          onClick={() => fileInputRef.current?.click()}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50"
        >
          Upload file
        </button>
        {uploading && (
          <span
            data-testid="file-uploading"
            role="status"
            className="text-sm text-gray-600 dark:text-gray-400"
          >
            Uploading… {uploading}
          </span>
        )}
        <input
          ref={fileInputRef}
          type="file"
          data-testid="file-upload-input"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void handleUpload(file)
          }}
        />
        {path !== '/' && (
          <button
            type="button"
            data-testid="file-up-button"
            onClick={() => setPath(parentPath(path))}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm"
          >
            Up one level
          </button>
        )}
        <button
          type="button"
          data-testid="file-refresh-button"
          onClick={() => void load(path)}
          className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm"
        >
          Refresh
        </button>
      </div>

      {pendingReplace && (
        <div
          data-testid="file-overwrite-confirm"
          role="alertdialog"
          className="rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-800 p-4 text-sm text-yellow-900 dark:text-yellow-200 flex flex-wrap items-center gap-3"
        >
          <span>
            A file named "{pendingReplace.name}" already exists here. Replace it with the file you
            selected? The current contents will be lost.
          </span>
          <button
            type="button"
            data-testid="file-overwrite-replace"
            onClick={confirmReplace}
            className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium"
          >
            Replace
          </button>
          <button
            type="button"
            data-testid="file-overwrite-cancel"
            onClick={cancelReplace}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700"
          >
            Cancel
          </button>
        </div>
      )}

      {error && (
        <div
          data-testid="file-manager-error"
          role="alert"
          className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {notice && (
        <div
          data-testid="file-manager-notice"
          className="rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 text-sm text-green-700 dark:text-green-300"
        >
          {notice}
        </div>
      )}

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        {loading ? (
          <p data-testid="file-loading" className="p-6 text-sm text-gray-500 dark:text-gray-400">
            Loading files…
          </p>
        ) : listFailed ? (
          path === '/' && listFailed.status === 404 ? (
            <p data-testid="file-manager-home-missing" className="p-6 text-sm text-gray-600 dark:text-gray-400">
              Your home folder isn't ready yet. This is not something you can fix from here — contact your hosting provider.
            </p>
          ) : null
        ) : entries.length === 0 ? (
          <div data-testid="file-empty-state" className="p-8 text-center">
            <p className="text-gray-900 dark:text-gray-100 font-medium">
              This folder is empty
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              Upload a file to get started, or open one of your domain folders.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="file-table" className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300">Name</th>
                  <th className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300">Size</th>
                  <th className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300">Modified</th>
                  <th className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.name}
                    data-testid={`file-row-${entry.name}`}
                    className="border-t border-gray-100 dark:border-gray-800"
                  >
                    <td className="px-4 py-2">
                      {isDirectory(entry) ? (
                        <button
                          type="button"
                          onClick={() => setPath(joinPath(path, entry.name))}
                          className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                        >
                          {entry.name}/
                        </button>
                      ) : (
                        <span className="text-gray-900 dark:text-gray-100">{entry.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                      {isDirectory(entry) ? '—' : formatSize(entry.size)}
                    </td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                      {formatTime(entry.mtime)}
                    </td>
                    <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                      {!isDirectory(entry) && (
                        <button
                          type="button"
                          data-testid={`file-download-${entry.name}`}
                          disabled={busy === entry.name}
                          onClick={() => void handleDownload(entry)}
                          className="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                        >
                          Download
                        </button>
                      )}
                      {!isDirectory(entry) && entry.name.toLowerCase().endsWith('.zip') && (
                        <button
                          type="button"
                          data-testid={`file-extract-${entry.name}`}
                          disabled={busy === entry.name}
                          onClick={() => void handleExtract(entry)}
                          className="text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                        >
                          Extract
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid={`file-delete-${entry.name}`}
                        disabled={busy === entry.name}
                        onClick={() => void handleDelete(entry)}
                        className="text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
